import { FILE_LIMITS, SUPPORTED_FORMATS } from '../app/constants'
import type { FileKind, FileMeta } from '../app/types'

const MIME_KIND: Record<string, FileKind> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'text/markdown': 'md',
  'text/html': 'html',
  'text/csv': 'csv',
  'application/json': 'json',
  'text/plain': 'txt',
  'application/pdf': 'pdf',
}

export function detectFileKind(file: File): FileKind {
  const name = file.name.toLowerCase()

  if (name.endsWith('.lottie')) {
    return 'lottie'
  }

  for (const format of SUPPORTED_FORMATS) {
    if (format.extensions.some((extension) => name.endsWith(extension))) {
      return format.kind
    }
  }

  if (name.endsWith('.pdf')) {
    return 'pdf'
  }

  return MIME_KIND[file.type] ?? 'unknown'
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`
  }

  const units = ['KB', 'MB', 'GB']
  let value = bytes / 1024
  let unitIndex = 0

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024
    unitIndex += 1
  }

  return `${value.toFixed(value >= 10 ? 1 : 2)} ${units[unitIndex]}`
}

export function getSizeWarnings(file: File): string[] {
  if (file.size > FILE_LIMITS.largeBytes) {
    return ['文件超过 30 MB，低内存手机可能只能尝试只读或分段加载。']
  }

  if (file.size > FILE_LIMITS.ordinaryBytes) {
    return ['文件较大，解析和导出可能需要更久。']
  }

  return []
}

export async function computeFingerprint(file: File): Promise<string> {
  const head = await file.slice(0, 64 * 1024).arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', head)
  const hash = Array.from(new Uint8Array(digest))
    .slice(0, 12)
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('')

  return `${file.name}:${file.size}:${file.lastModified}:${hash}`
}

export async function createFileMeta(file: File): Promise<FileMeta> {
  const kind = detectFileKind(file)
  const fingerprint = await computeFingerprint(file)
  const id = `${kind}-${fingerprint}`

  return {
    id,
    name: file.name,
    kind,
    size: file.size,
    mime: file.type || 'application/octet-stream',
    lastModified: file.lastModified,
    fingerprint,
    openedAt: Date.now(),
    warnings: getSizeWarnings(file),
  }
}

export function extensionOf(filename: string): string {
  const match = filename.toLowerCase().match(/\.[^.]+$/)
  return match?.[0] ?? ''
}
